import React, { useState } from 'react';
import {
  IAvailableWorkflowActions,
  IFiguresAndTablesFile
} from '@publishing-engine/domain/models';
import { Flex } from '@chakra-ui/react';
import { FormFieldGraphicalAbstractImage } from '@publishing-engine/domain/types/article';
import { FieldHeader } from '../common/FieldHeader';
import GraphicalAbstractImageUpdateModal from './GraphicalAbstractImageUpdateModal';
import { EntryPoint } from 'src/utils/types';
import { useSubmissionFields } from 'src/hooks/useSubmissionFields';
import { GraphicalAbstractImageDiffModal } from './GraphicalAbstractImageDiffModal';
import GraphicalAbstractImageDisplay from './GraphicalAbstractImageDisplay';
import { GraphicalAbstractImageEmptyState } from './GraphicalAbstractImageEmptyState';

interface Props {
  formFieldDefinition: FormFieldGraphicalAbstractImage;
  value: IFiguresAndTablesFile;
  previousValue?: IFiguresAndTablesFile;
  isEditable: boolean;
  onChange: (value: IFiguresAndTablesFile) => void;
  submissionContextId: string;
  availableActions: IAvailableWorkflowActions;
  entryPoint: EntryPoint;
  isUpdateRequired?: boolean;
}

export const GraphicalAbstractImage: React.FC<Props> = ({
  formFieldDefinition,
  value,
  previousValue,
  isEditable,
  submissionContextId,
  availableActions,
  onChange,
  entryPoint,
  isUpdateRequired
}) => {
  const { checkFormFieldValueIsUpdated } = useSubmissionFields(entryPoint);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const isUpdated = checkFormFieldValueIsUpdated(formFieldDefinition.name);

  const editCallback = isEditable
    ? () => {
        setIsEditing(true);
      }
    : undefined;

  const [showDiffModal, setShowDiffModal] = useState<boolean>(false);
  const openDiffModal = () => setShowDiffModal(true);
  const closeDiffModal = () => setShowDiffModal(false);
  const hasValue = !!value?.length;

  return (
    <Flex flexDirection={'column'} gap={2} id="Graphical Abstract">
      {isEditing && (
        <GraphicalAbstractImageUpdateModal
          isOpen={isEditing}
          formField={formFieldDefinition}
          onClose={() => setIsEditing(false)}
          value={value}
          onSubmit={value => {
            setIsEditing(false);
            onChange(value.fileSet?.GraphicalAbstractImage ?? []);
          }}
          submissionContextId={submissionContextId}
        />
      )}

      {isUpdated && showDiffModal ? (
        <GraphicalAbstractImageDiffModal
          config={formFieldDefinition}
          value={value}
          previousValue={previousValue}
          onClose={closeDiffModal}
        />
      ) : null}

      <FieldHeader
        {...formFieldDefinition}
        label={`${formFieldDefinition.params.labels.main} (${
          value?.length ?? 0
        })`}
        isUpdated={isUpdated}
        editCallback={editCallback}
        onShowDiffModal={openDiffModal}
        availableActions={availableActions}
        entryPoint={entryPoint}
        isUpdateRequired={isUpdateRequired}
      />

      {hasValue ? (
        <GraphicalAbstractImageDisplay images={value} />
      ) : (
        <GraphicalAbstractImageEmptyState
          fieldLabels={formFieldDefinition.params.labels}
        />
      )}
    </Flex>
  );
};
